import React, { useCallback, useMemo, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useMediaQuery } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useMergedAppointment } from '../../../../context/AppointmentOverridesContext';
import { useEligibilityDrawer } from '../../../../context/EligibilityDrawerContext';

import { BaseEvent } from './components/BaseEvent';
import { ExpandedCard } from './components/ExpandedCard';
import { getEventIcon } from './utils/getEventIcon';
import { useActionMessage, useEventStyling, useReadinessStyling } from './hooks';
import type { EventComponentProps } from './types';

// Hover timing — see eligibility-prototype/MULTI-PROVIDER-CALENDAR-RESEARCH
// (companion doc) and the dense-popover research:
//   restMs   — cursor must be still this long over the event before opening
//              (intentional hover signal in dense grids)
//   open     — fallback delay when restMs never trips
//   close    — short delay; the safe polygon protects the trip into the popover
const HOVER_REST_MS = 120;
const HOVER_OPEN_MS = 450;
const HOVER_CLOSE_MS = 100;

/**
 * Public entrypoint. Each event mounts its own `<FloatingTree>` so the hover
 * popover and any nested `EditablePropertyRow` popovers (rendered via
 * `AppointmentPropertiesTable`) can coordinate dismissal — clicks/hovers
 * inside the row editor count as "inside the tree" and don't accidentally
 * close the parent hover popover.
 *
 * Outside the tree, `EditablePropertyRow` is rendered standalone (no tree
 * coordination needed) — see the `parentId !== null` guard there.
 */
export const EventComponent = (props: EventComponentProps) => {
  // If a parent already mounted a tree (unlikely, but possible if events ever
  // get nested), reuse it rather than nesting trees.
  const parentId = useFloatingParentNodeId();
  if (parentId !== null) {
    return <EventComponentInner {...props} />;
  }
  return (
    <FloatingTree>
      <EventComponentInner {...props} />
    </FloatingTree>
  );
};

const EventComponentInner = ({
  event,
  viewMode,
}: EventComponentProps) => {
  const nodeId = useFloatingNodeId();
  const [isOpen, setIsOpen] = useState(false);
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);

  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
  const { openDrawer } = useEligibilityDrawer();
  const { appointment: appointmentTracking } = useEventTracking();

  // Apply in-flight overrides so the calendar block reflects in-session edits
  // (notes added via drawer or hover-popover surface here).
  const mergedAppt = useMergedAppointment(event.resource);
  const mergedEvent = useMemo(() => ({ ...event, resource: mergedAppt }), [event, mergedAppt]);

  const { variant, cssVars: variantCssVars } = useEventStyling(mergedEvent);
  const readiness = mergedAppt.block_state?.readiness ?? null;
  const readinessCssVars = useReadinessStyling(readiness);
  const cssVars = { ...variantCssVars, ...readinessCssVars };
  const actionMessage = useActionMessage(mergedEvent);
  const icon = getEventIcon(mergedEvent);

  // Floating UI: positioning + hover lifecycle. Open state is fully owned here;
  // the safe polygon middleware keeps the popover open while the cursor is in
  // the trigger→popover transit area, with intent gating to avoid drive-by
  // sweeps over neighboring events.
  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right-start',
    middleware: [
      offset(12),
      flip({ fallbackPlacements: ['left-start', 'bottom-start', 'top-start'] }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !isTouch,
    restMs: HOVER_REST_MS,
    delay: { open: HOVER_OPEN_MS, close: HOVER_CLOSE_MS },
    handleClose: safePolygon({
      requireIntent: true,
      blockPointerEvents: true,
    }),
  });
  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: true });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  // Click handler — every event opens the drawer. Drawer renders the
  // Properties table for all events; eligibility-specific sections (hero,
  // cost, plan) only render when block_state.readiness / snapshot are present.
  const handleEventClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsOpen(false);
      openDrawer(mergedAppt);

      // Preserved analytics for actionable variants — surface still tracks
      // cancelled/suggested/rescheduling clicks.
      if (variant === 'actionable') {
        const reality = mergedAppt.block_state?.reality;
        const actionableType =
          reality === 'suggested'
            ? 'suggested'
            : reality === 'cancelled'
              ? 'cancelled'
              : reality === 'rescheduling'
                ? 'reschedule_requested'
                : null;
        if (actionableType) {
          appointmentTracking.eventQuickActionOpened({
            appointment_id: mergedAppt.appointment_id,
            actionable_event_type: actionableType,
            view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
            is_touch_device: isTouch,
          });
        }
      }
    },
    [variant, mergedAppt, openDrawer, appointmentTracking, viewMode, isTouch],
  );

  const referenceProps = getReferenceProps({ onClick: handleEventClick });

  return (
    <FloatingNode id={nodeId}>
      <BaseEvent
        event={mergedEvent}
        title={mergedEvent.title}
        viewMode={viewMode}
        eventRef={refs.setReference}
        referenceProps={referenceProps}
        sx={cssVars}
      />

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1000 }}
            {...getFloatingProps({
              onMouseEnter: () => setIsPopoverHovered(true),
              onMouseLeave: () => setIsPopoverHovered(false),
            })}
          >
            <ExpandedCard
              event={mergedEvent}
              icon={icon}
              cssVars={cssVars}
              variant={variant}
              actionMessage={actionMessage}
              isPopoverHovered={isPopoverHovered}
              onOpenInDrawer={() => {
                setIsOpen(false);
                openDrawer(mergedAppt);
              }}
            />
          </div>
        </FloatingPortal>
      )}
    </FloatingNode>
  );
};
